import React, {useEffect} from 'react';
import {BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useSearchParams,} from 'react-router-dom';

import NoMatch from './components/nomatch';
import Login from './components/login/login';
import PasswordResetWrapper from './components/password-reset/password-reset-wrapper';
import PasswordResetForm from './components/password-reset/password-reset-form';
import PasswordResetRequest from './components/password-reset/password-reset-request';
import LoginRequired from './util/LoginRequired';
import Registration from './components/registration/registration';
import RegistrationBasicForm from './components/registration/registration-basic-form';
import RegistrationActivationForm from './components/registration/registration-activation-form';
import DashboardWrapper from './components/dashboard/dashboard-wrapper';
import ComponentContainer from './components/component-container';
import CreateObject from './components/objects/create-object';
import EditObject from './components/objects/edit-object';
import Objects from './components/objects/objects';
import ObjectsDetailsContainer from './components/objects/object-details-container';
import ObjectsOverview from './components/objects/objects-overview';
import ObjectDetails from './components/objects/object-details';
import LocationsDetails from './components/locations/location-details';
import LocationsDetailsContainer from './components/locations/location-details-container';
import CreateLocation from './components/locations/create-location';
import EditLocation from './components/locations/edit-location';
import ApplianceDetailContainer from './components/appliances/appliance-details-container';
import CreateAppliance from './components/appliances/create-appliance';
import EditAppliance from './components/appliances/edit-appliance';
import EditProfile from './components/profile/profile';
import Turnover from './components/turnover/turnover';
import ReturningPage from './components/returning/returning-page';
import PartnersOverview from './components/realms/partners-overview';
import CreatePartnerBasicForm from './components/realms/create-partner-basic-form';
import Downloads from './components/downloads/downloads';
import {MaybeLoadCountries} from './store/countriesDetailsSlice';
import PartnerUsageData from './components/usage-data/partner-usage-data';
import DetailedUsageDataScreen from './components/usage-data/detailed-usage-data-screen';
import LocationApaleoPropertyScreen from './components/locations/location-apaleo-property-screen';
import {RefreshToken} from './store/authSlice';
import ManageAccountWrapper from './components/account/manage-account-wrapper';
import ChangeEmailLinkPageWrapper from './components/account/change-email-link-wrapper';
import LoginActivationScreen from './components/login-activation/login-activation-screen';
import VipUserPricing from './components/locations/vip-user-pricing';
import LocationPricingScreen from './components/locations/prices/location-pricing-screen';
import VipSelectLaundryRoomWrapper from './components/vip/vip-select-laundry-room-wrapper';
import AccountingAreaRedirect from './util/AccountingAreaRedirect';
import SelectLocationPricingScreen from './components/locations/prices/select-location-pricing-screen';
import SelectLocationSuggestedPriceScreen from './components/locations/prices/select-location-suggested-price-screen';
import LocationSuggestedPricesScreen from './components/locations/prices/location-suggested-prices-screen';
import RolesOverviewWrapper from './components/realms/roles-overview-wrapper';
import RolesInviteWrapper from './components/realms/roles-invite-wrapper';
import withAuthorization from './nav/withAuthorization';
import PartnerInvoices from './components/partner-invoices/partner-invoices';
import OperationalContactsScreen from './components/operational-contacts/operational-contacts-screen';
import OperationalContactEditWrapper from './components/operational-contacts/operational-contact-edit-wrapper';
import OperationalContactCreate from './components/operational-contacts/operational-contact-create';
import {usePbDispatch, usePbSelector} from './store/pbStoreHooks';
import NavigationRegistrar from './nav/NavigationRegistrar';

function RedirectIfAuthed({loggedIn}: Readonly<{ loggedIn: boolean }>) {
    if (loggedIn) return <Navigate to="/dashboard" replace />;
    return <Login />;
}

function RequireAuth({loggedIn}: Readonly<{ loggedIn: boolean }>) {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    if (searchParams.get('token')) {
        return <Navigate to="/dashboard" replace />;
    }

    if (!loggedIn) {
        return (
            <Navigate
                to="/login"
                replace
                state={{ nextPathname: location.pathname }}
            />
        );
    }

    return <Outlet />;
}

function RequireAccountingArea({
    loggedIn,
    selectedPartnerId,
                               }: Readonly<{
    loggedIn: boolean;
    selectedPartnerId: string;
}>) {
    if (!loggedIn) return <Navigate to="/login" replace />;

    if (!selectedPartnerId) {
        return <Navigate to="/select-partner" replace />;
    }

    return (
        <LoginRequired>
            <Outlet />
        </LoginRequired>
    );
}

export default function AppRouter() {
    const dispatch = usePbDispatch();

    const loggedIn = usePbSelector(
        (store) => store.auth.authentication.loggedIn
    );
    const selectedPartnerId = usePbSelector(
        (store) => store.partnerManager.selectedPartnerId
    );

    useEffect(() => {
        dispatch(MaybeLoadCountries());
        dispatch(RefreshToken());
    }, [dispatch]);

    const ProfileWithAuth = withAuthorization(
        EditProfile,
        'MASTER_CONTRACT_DATA'
    );
    const RolesOverviewWrapperWithAuth = withAuthorization(
        RolesOverviewWrapper,
        'GENERAL_ACCESS_MANAGEMENT'
    );
    const RolesInviteWrapperWithAuth = withAuthorization(
        RolesInviteWrapper,
        'GENERAL_ACCESS_MANAGEMENT'
    );
    const OperationalContactsScreenWithAuth = withAuthorization(
        OperationalContactsScreen,
        'GENERAL_OPERATIONAL_CONTACTS'
    );
    const OperationalContactCreateWithAuth = withAuthorization(
        OperationalContactCreate,
        'GENERAL_OPERATIONAL_CONTACTS'
    );
    const OperationalContactEditWrapperWithAuth = withAuthorization(
        OperationalContactEditWrapper,
        'GENERAL_OPERATIONAL_CONTACTS'
    );
    const PartnerUsageDataWithAuth = withAuthorization(
        PartnerUsageData,
        'ACCOUNTING_NUMBERS'
    );
    const DetailedUsageDataScreenWithAuth = withAuthorization(
        DetailedUsageDataScreen,
        'ACCOUNTING_NUMBERS'
    );
    const VipSelectLaundryRoomWrapperWithAuth = withAuthorization(
        VipSelectLaundryRoomWrapper,
        'RESIDENTIAL_LOCATION'
    );
    const SelectLocationPricingScreenWithAuth = withAuthorization(
        SelectLocationPricingScreen,
        'RESIDENTIAL_LOCATION'
    );
    const SelectLocationSuggestedPriceScreenWithAuth = withAuthorization(
        SelectLocationSuggestedPriceScreen,
        'RESIDENTIAL_LOCATION'
    );
    const TurnoverWithAuth = withAuthorization(
        Turnover,
        'ACCOUNTING_CREDIT_NOTES'
    );
    const PartnerInvoicesWithAuth = withAuthorization(
        PartnerInvoices,
        'ACCOUNTING_PARTNER_INVOICES'
    );
    const ObjectsWithAuth = withAuthorization(Objects, 'RESIDENTIAL_MANSION');
    const ObjectsOverviewWithAuth = withAuthorization(
        ObjectsOverview,
        'RESIDENTIAL_MANSION'
    );
    const CreateObjectWithAuth = withAuthorization(
        CreateObject,
        'RESIDENTIAL_MANSION'
    );
    const ObjectsDetailsContainerWithAuth = withAuthorization(
        ObjectsDetailsContainer,
        'RESIDENTIAL_MANSION'
    );
    const EditObjectWithAuth = withAuthorization(
        EditObject,
        'RESIDENTIAL_MANSION'
    );
    const LocationsDetailsContainerWithAuth = withAuthorization(
        LocationsDetailsContainer,
        'RESIDENTIAL_LOCATION'
    );
    const LocationsDetailsWithAuth = withAuthorization(
        LocationsDetails,
        'RESIDENTIAL_LOCATION'
    );
    const CreateLocationWithAuth = withAuthorization(
        CreateLocation,
        'RESIDENTIAL_LOCATION'
    );
    const EditLocationWithAuth = withAuthorization(
        EditLocation,
        'RESIDENTIAL_LOCATION'
    );
    const VipUserPricingWithAuth = withAuthorization(
        VipUserPricing,
        'RESIDENTIAL_LOCATION'
    );
    const LocationPricingScreenWithAuth = withAuthorization(
        LocationPricingScreen,
        'RESIDENTIAL_LOCATION'
    );
    const LocationSuggestedPricesScreenWithAuth = withAuthorization(
        LocationSuggestedPricesScreen,
        'RESIDENTIAL_LOCATION'
    );
    const LocationApaleoPropertyScreenWithAuth = withAuthorization(
        LocationApaleoPropertyScreen,
        'RESIDENTIAL_LOCATION'
    );
    const CreateApplianceWithAuth = withAuthorization(
        CreateAppliance,
        'RESIDENTIAL_APPLIANCE'
    );
    const ApplianceDetailContainerWithAuth = withAuthorization(
        ApplianceDetailContainer,
        'RESIDENTIAL_APPLIANCE'
    );
    const EditApplianceWithAuth = withAuthorization(
        EditAppliance,
        'RESIDENTIAL_APPLIANCE'
    );

    return (
        <BrowserRouter>
            <NavigationRegistrar />
            <Routes>
                <Route path="/" element={<ComponentContainer />}>
                    <Route
                        index
                        element={<Navigate to="/dashboard" replace />}
                    />

                    <Route
                        path="login"
                        element={<RedirectIfAuthed loggedIn={loggedIn} />}
                    />

                    <Route
                        path="change-email/:code"
                        element={<ChangeEmailLinkPageWrapper />}
                    />
                    <Route
                        path="activate-account/:key"
                        element={<LoginActivationScreen />}
                    />

                    <Route
                        path="password-reset"
                        element={<PasswordResetWrapper />}
                    >
                        <Route path="set" element={<PasswordResetForm />} />
                        <Route
                            path="request"
                            element={<PasswordResetRequest />}
                        />
                    </Route>

                    <Route path="returning" element={<ReturningPage />} />

                    <Route path="registration" element={<Registration />}>
                        <Route index element={<RegistrationBasicForm />} />
                        <Route
                            path="activate"
                            element={<RegistrationActivationForm />}
                        />
                    </Route>

                    <Route element={<RequireAuth loggedIn={loggedIn} />}>
                        <Route
                            path="select-partner"
                            element={<PartnersOverview />}
                        />
                        <Route
                            path="create-partner"
                            element={<CreatePartnerBasicForm />}
                        />
                        <Route
                            path="manage-account"
                            element={<ManageAccountWrapper />}
                        />

                        <Route
                            path="accounting-areas/:accounting_area_id/*"
                            element={<AccountingAreaRedirect />}
                        />

                        <Route
                            element={
                                <RequireAccountingArea
                                    loggedIn={loggedIn}
                                    selectedPartnerId={selectedPartnerId}
                                />
                            }
                        >
                            <Route
                                path="dashboard"
                                element={<DashboardWrapper />}
                            />

                            <Route
                                path="profile"
                                element={<ProfileWithAuth />}
                            />

                            <Route
                                path="roles"
                                element={<RolesOverviewWrapperWithAuth />}
                            />
                            <Route
                                path="roles/invitation"
                                element={<RolesInviteWrapperWithAuth />}
                            />

                            <Route path="operational-contacts">
                                <Route
                                    index
                                    element={
                                        <OperationalContactsScreenWithAuth />
                                    }
                                />
                                <Route
                                    path="create"
                                    element={
                                        <OperationalContactCreateWithAuth />
                                    }
                                />
                                <Route
                                    path=":contact_id"
                                    element={
                                        <OperationalContactEditWrapperWithAuth />
                                    }
                                />
                            </Route>

                            <Route path="usage-data">
                                <Route
                                    index
                                    element={<PartnerUsageDataWithAuth />}
                                />
                                <Route
                                    path=":contract_period"
                                    element={
                                        <DetailedUsageDataScreenWithAuth />
                                    }
                                />
                            </Route>

                            <Route
                                path="discounted-use"
                                element={
                                    <VipSelectLaundryRoomWrapperWithAuth />
                                }
                            />

                            <Route
                                path="select-location-pricing"
                                element={
                                    <SelectLocationPricingScreenWithAuth />
                                }
                            />
                            <Route
                                path="select-location-suggested-prices"
                                element={
                                    <SelectLocationSuggestedPriceScreenWithAuth />
                                }
                            />

                            <Route
                                path="turnover"
                                element={<TurnoverWithAuth />}
                            />
                            <Route
                                path="partner-invoices"
                                element={<PartnerInvoicesWithAuth />}
                            />

                            <Route path="downloads" element={<Downloads />} />

                            <Route path="objects" element={<ObjectsWithAuth />}>
                                <Route
                                    index
                                    element={<ObjectsOverviewWithAuth />}
                                />
                                <Route
                                    path="create-object"
                                    element={<CreateObjectWithAuth />}
                                />

                                <Route
                                    path=":object_id"
                                    element={
                                        <ObjectsDetailsContainerWithAuth />
                                    }
                                >
                                    <Route index element={<ObjectDetails />} />
                                    <Route
                                        path="create-location"
                                        element={<CreateLocationWithAuth />}
                                    />
                                    <Route
                                        path="overview"
                                        element={<EditObjectWithAuth />}
                                    />

                                    <Route
                                        path=":location_id"
                                        element={
                                            <LocationsDetailsContainerWithAuth />
                                        }
                                    >
                                        <Route
                                            index
                                            element={
                                                <LocationsDetailsWithAuth />
                                            }
                                        />
                                        <Route
                                            path="create-appliance"
                                            element={
                                                <CreateApplianceWithAuth />
                                            }
                                        />
                                        <Route
                                            path="overview"
                                            element={<EditLocationWithAuth />}
                                        />
                                        <Route
                                            path="vip-user-pricing"
                                            element={<VipUserPricingWithAuth />}
                                        />
                                        <Route
                                            path="pricing"
                                            element={
                                                <LocationPricingScreenWithAuth />
                                            }
                                        />
                                        <Route
                                            path="suggested-prices"
                                            element={
                                                <LocationSuggestedPricesScreenWithAuth />
                                            }
                                        />
                                        <Route
                                            path="apaleo-property"
                                            element={
                                                <LocationApaleoPropertyScreenWithAuth />
                                            }
                                        />

                                        <Route
                                            path=":appliance_id"
                                            element={
                                                <ApplianceDetailContainerWithAuth />
                                            }
                                        >
                                            <Route
                                                index
                                                element={
                                                    <Navigate
                                                        to="overview"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="overview"
                                                element={
                                                    <EditApplianceWithAuth />
                                                }
                                            />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                    </Route>

                    <Route path="*" element={<NoMatch />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}
